import { zhTranFn } from '../user-center/src/utils/zh.ts'

export const DEFAULT_RISK_KEYWORDS = []
export const DEFAULT_BRAND_KEYWORDS = []

const SHORT_ASCII_KEYWORD_PATTERN = /^[a-z0-9]{1,3}$/i
const ASCII_WORD_CHAR_PATTERN = /[a-z0-9]/i

const NOISE_PATTERN
  = /[\s\-_/\\|,.;:!?'"`~@#$%^&*+=<>()[\]{}，。；：！？、（）【】《》“”‘’·]+/g

function parseObject(value) {
  if (!value)
    return {}

  if (typeof value === 'object' && !Array.isArray(value))
    return value

  if (typeof value !== 'string')
    return {}

  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  }
  catch {
    return {}
  }
}

function getFilterConfig(value) {
  const source = parseObject(value)
  const directConfig = parseObject(source.trafficItemFilter)
  if (Object.keys(directConfig).length)
    return directConfig

  const reactAppConfig = parseObject(source.reactAppConfig)
  return parseObject(reactAppConfig.trafficItemFilter)
}

export function resolveTrafficItemFilterConfig(...sources) {
  for (const sourceValue of sources.flat()) {
    const source = parseObject(sourceValue)
    const candidates = [
      source,
      source.otherJson,
      source.storePageInfo,
      parseObject(source.storePageInfo).otherJson,
      source.offiaccountFollowData,
      parseObject(source.offiaccountFollowData).otherJson,
      source.offiaccountFollowUrlData,
      parseObject(source.offiaccountFollowUrlData).otherJson,
      source.offiaccountFollowUrl,
      parseObject(source.offiaccountFollowUrl).otherJson,
    ]

    for (const candidate of candidates) {
      const config = getFilterConfig(candidate)
      if (Object.keys(config).length) {
        return {
          brandKeywords: normalizeKeywordList(config.brandKeywords),
          riskKeywords: normalizeKeywordList(config.riskKeywords),
          useDefaultBrandKeywords: config.useDefaultBrandKeywords !== false,
          useDefaultRiskKeywords: config.useDefaultRiskKeywords !== false,
        }
      }
    }
  }

  return {}
}

function normalizeText(value = '') {
  return zhTranFn(String(value || ''), 's')
    .normalize('NFKC')
    .trim()
    .toLowerCase()
}

function includesBoundedAsciiKeyword(sourceText, keyword) {
  let matchIndex = sourceText.indexOf(keyword)

  while (matchIndex >= 0) {
    const previousCharacter = sourceText[matchIndex - 1] || ''
    const nextCharacter = sourceText[matchIndex + keyword.length] || ''
    const hasStartBoundary = !ASCII_WORD_CHAR_PATTERN.test(previousCharacter)
    const hasEndBoundary = !ASCII_WORD_CHAR_PATTERN.test(nextCharacter)

    if (hasStartBoundary && hasEndBoundary)
      return true

    matchIndex = sourceText.indexOf(keyword, matchIndex + 1)
  }

  return false
}

function prepareKeywords(config) {
  const { brandKeywords, riskKeywords } = resolveGoodsFilterKeywords(config)
  return [...brandKeywords, ...riskKeywords].map((keyword) => {
    const normalized = normalizeText(keyword)
    const collapsed = normalized.replace(NOISE_PATTERN, '')
    return {
      normalized,
      collapsed,
      boundedAscii: SHORT_ASCII_KEYWORD_PATTERN.test(collapsed),
    }
  })
}

function matchesTitle(sourceText, keywords) {
  // Convert each title once, rather than repeating it for every keyword.
  const normalizedSource = normalizeText(sourceText)
  const collapsedSource = normalizedSource.replace(NOISE_PATTERN, '')

  return keywords.some((keyword) => {
    if (!keyword.normalized)
      return false

    // Preserve boundaries for short aliases, including noise removal for C-K.
    if (keyword.boundedAscii) {
      return includesBoundedAsciiKeyword(normalizedSource, keyword.normalized)
        || includesBoundedAsciiKeyword(collapsedSource, keyword.collapsed)
    }

    return normalizedSource.includes(keyword.normalized)
      || (Boolean(keyword.collapsed) && collapsedSource.includes(keyword.collapsed))
  })
}

export function normalizeKeywordList(keywords) {
  if (!Array.isArray(keywords))
    return []

  const seen = new Set()
  return keywords.reduce((result, item) => {
    const text = String(item || '').trim()
    const normalizedKey = normalizeText(text)
    if (text && !seen.has(normalizedKey)) {
      seen.add(normalizedKey)
      result.push(text)
    }
    return result
  }, [])
}

export function resolveGoodsFilterKeywords(config = {}) {
  const {
    brandKeywords = [],
    riskKeywords = [],
    useDefaultBrandKeywords = true,
    useDefaultRiskKeywords = true,
  } = config || {}

  return {
    brandKeywords: normalizeKeywordList([
      ...(useDefaultBrandKeywords ? DEFAULT_BRAND_KEYWORDS : []),
      ...brandKeywords,
    ]),
    riskKeywords: normalizeKeywordList([
      ...(useDefaultRiskKeywords ? DEFAULT_RISK_KEYWORDS : []),
      ...riskKeywords,
    ]),
  }
}

export function shouldFilterTrafficItem(item, config = {}) {
  return matchesTrafficItem(item, prepareKeywords(config))
}

function matchesTrafficItem(item, keywords) {
  if (!keywords.length)
    return false

  const titles = [
    item?.title,
    item?.subject,
    item?.subjectTrans,
    item?.multiLanguageInfo?.title,
    item?.itemName,
    item?.item_name,
  ].map(title => String(title || '')).filter(Boolean)
  return titles.some(title => matchesTitle(title, keywords))
}

export function filterTrafficItems(items, config = {}) {
  const filteredItems = []
  let filteredCount = 0
  const keywords = prepareKeywords(config)

  for (const item of items || []) {
    if (matchesTrafficItem(item, keywords)) {
      filteredCount += 1
    }
    else {
      filteredItems.push(item)
    }
  }

  return { items: filteredItems, filteredCount }
}

const TRAFFIC_ITEM_LIST_PATHS = [
  ['data', 'dataList'],
  ['data', 'data', 'dataList'],
  ['data', 'data', 'data', 'dataList'],
  ['data', 'records'],
  ['data', 'list'],
  ['data', 'itemList'],
  ['data', 'items'],
  ['data', 'data'],
  ['data'],
]

function getAtPath(source, path) {
  return path.reduce((current, key) => current?.[key], source)
}

function setAtPath(source, path, value) {
  const parent = path.slice(0, -1).reduce((current, key) => current?.[key], source)
  if (parent && typeof parent === 'object')
    parent[path[path.length - 1]] = value
}

export function filterTrafficItemListResponse(responseData, config = {}) {
  if (!responseData || typeof responseData !== 'object')
    return responseData

  const listPath = TRAFFIC_ITEM_LIST_PATHS.find(path => Array.isArray(getAtPath(responseData, path)))
  if (!listPath)
    return responseData

  const result = filterTrafficItems(getAtPath(responseData, listPath), config)
  setAtPath(responseData, listPath, result.items)
  return responseData
}
